import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { auth } from './Firebase';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Helmet } from 'react-helmet-async';
import CustomSelect from './CustomSelect';

const convertUTCToLocal = (utcValue) => {
  if (!utcValue) return '';
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  } else {
    return '';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

const getGradientColor = (value, maxValue) => {
  if (value === 0 || !maxValue) return 'rgba(255, 255, 255, 0.3)'; 
  const scaledValue = Math.min(1, Math.max(0, value / maxValue));
  
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); 
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';
  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const WcMatchesProbabilities = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [orderBy, setOrderBy] = useState('probability');
  const [order, setOrder] = useState('desc');
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  
  const [selectedStageId, setSelectedStageId] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState(''); // Stan dla filtra drużyn

  const simulationOptions = simulations.map((sim) => ({
    value: sim.simulation_id,
    label: sim.displayName
  }));

  // 1. Pobieranie listy symulacji
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/competition/431/simulations`)
      .then((response) => {
        const sims = response.data || [];
        const simList = sims.map(sim => ({
          ...sim,
          userId: sim.user_id, 
          displayName: sim.conditions && sim.conditions !== "" 
            ? `${convertUTCToLocal(sim.created_at)} (*)` 
            : convertUTCToLocal(sim.created_at)
        }));

        const filteredSimList = simList.filter(sim => {
          const isDefault = !sim.userId || sim.userId === ""; 
          const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid;
          return isDefault || isOwned;
        });

        setSimulations(filteredSimList);

        if (filteredSimList.length > 0) {
          setSelectedSimulationId(filteredSimList[0].simulation_id);
        }
      })
      .catch((err) => console.error('Błąd pobierania listy symulacji:', err));
  }, [auth.currentUser]);

  // 2. Pobieranie danych par meczowych
  useEffect(() => {
    if (!selectedSimulationId) return;

    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/simulation/worldcup/matches-prob?simulationId=${selectedSimulationId}`)
      .then((response) => {
        setData(response.data);
        setLoading(false);
        setInitialLoading(false);
      })
      .catch((err) => {
        console.error('Błąd ładowania par meczowych MŚ:', err);
        setError('Nie udało się pobrać danych symulacji meczów.');
        setLoading(false);
        setInitialLoading(false);
      });
  }, [selectedSimulationId]);

  // 3. Wyodrębnienie dostępnych rund i unikalnych drużyn
  const availableStages = useMemo(() => {
    if (!data || !data.results) return [];
    
    const stageMap = new Map();
    data.results.forEach(match => {
      if (!stageMap.has(match.stageId)) {
        stageMap.set(match.stageId, match.stageName);
      }
    });

    return Array.from(stageMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => Number(a.id) - Number(b.id));
  }, [data]);

  const uniqueTeamsOptions = useMemo(() => {
    if (!data || !data.results) return [];
    const teams = new Set();
    data.results.forEach(match => {
      teams.add(match.homeTeamName);
      teams.add(match.awayTeamName);
    });

    const sortedTeams = Array.from(teams).sort((a, b) => a.localeCompare(b));
    return [
      { value: '', label: 'Wszystkie drużyny' },
      ...sortedTeams.map(t => ({ value: t, label: t }))
    ];
  }, [data]);

  useEffect(() => {
    if (availableStages.length > 0) {
      if (!availableStages.find(s => s.id === selectedStageId)) {
        setSelectedStageId(availableStages[0].id);
      }
    } else {
      setSelectedStageId('');
    }
  }, [availableStages, selectedStageId]);

  // 4. Nawigacja
  const currentSimulationIndex = simulations.findIndex(sim => sim.simulation_id === selectedSimulationId);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSimulationSelect = (event) => setSelectedSimulationId(event.target.value);
  const handleTeamFilterSelect = (event) => setSelectedTeamFilter(event.target.value);
  
  const handlePreviousSimulation = () => {
    if (currentSimulationIndex < simulations.length - 1) {
      setSelectedSimulationId(simulations[currentSimulationIndex + 1].simulation_id);
    }
  };
  const handleNextSimulation = () => {
    if (currentSimulationIndex > 0) {
      setSelectedSimulationId(simulations[currentSimulationIndex - 1].simulation_id);
    }
  };	

  // 5. Filtrowanie (Runda + Drużyna z inteligentną zamianą stron) i sortowanie
  const { sortedMatches, maxProbabilityInStage } = useMemo(() => {
    if (!data || !data.results || !selectedStageId) return { sortedMatches: [], maxProbabilityInStage: 1 };
    
    // Filtrujemy mecze wybranej rundy
    let stageMatches = data.results.filter(m => m.stageId === selectedStageId);

    // Filtrujemy po drużynie i układamy ją zawsze w pierwszej kolumnie
    if (selectedTeamFilter) {
      stageMatches = stageMatches
        .filter(m => m.homeTeamName === selectedTeamFilter || m.awayTeamName === selectedTeamFilter)
        .map(m => {
          // Jeśli wybrana drużyna jest w kolumnie "Away", zamieniamy je miejscami
          if (m.awayTeamName === selectedTeamFilter) {
            return {
              ...m,
              homeTeamName: m.awayTeamName,
              homeTeamId: m.awayTeamId,
              awayTeamName: m.homeTeamName,
              awayTeamId: m.homeTeamId
            };
          }
          return m;
        });
    }

    // Maksymalna szansa na potrzeby gradientu
    let maxProb = 0;
    stageMatches.forEach(m => {
      if (m.probability > maxProb) maxProb = m.probability;
    });
    const adjustedMaxProb = Math.max(maxProb, 0.05);

    // Sortowanie
    const sorted = [...stageMatches].sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];

      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      if (valueB < valueA) return order === 'desc' ? -1 : 1;
      if (valueB > valueA) return order === 'desc' ? 1 : -1;
      return 0;
    });

    return { sortedMatches: sorted, maxProbabilityInStage: adjustedMaxProb };
  }, [data, selectedStageId, selectedTeamFilter, orderBy, order]);

  // RENDEROWANIE
  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Typography variant="h6" color="text.secondary">Ładowanie par meczowych...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Helmet>
        <title>Potencjalne mecze - Mistrzostwa Świata | Symuliga</title>
      </Helmet>

      {/* Tytuł nagłówka */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Mistrzostwa Świata - Prawdopodobieństwo Par Meczowych
        </Typography>
        {simulations.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousSimulation} disabled={currentSimulationIndex >= simulations.length - 1} size="small">
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <CustomSelect value={selectedSimulationId} onChange={handleSimulationSelect} options={simulationOptions} />
            <IconButton onClick={handleNextSimulation} disabled={currentSimulationIndex === 0} size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {simulations[currentSimulationIndex]?.conditions !== "" && (
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
          Symulacja warunkowa!<br />
          <span dangerouslySetInnerHTML={{ __html: simulations[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
        </Typography>
      )}

      {/* Menu kafelkowe wyboru Rundy */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: 3, mt: 2 }}>
        {availableStages.map((stage) => (
          <Button
            key={stage.id}
            variant={selectedStageId === stage.id ? 'contained' : 'outlined'}
            onClick={() => setSelectedStageId(stage.id)}
            sx={{
              color: selectedStageId === stage.id ? '#000' : '#fff',
              backgroundColor: selectedStageId === stage.id ? '#ffd700' : 'transparent',
              borderColor: '#ffd700',
              fontWeight: 'bold',
              minWidth: '110px',
              '&:hover': {
                backgroundColor: selectedStageId === stage.id ? '#ffc107' : 'rgba(255, 215, 0, 0.1)',
                borderColor: '#ffc107',
              }
            }}
          >
            {stage.name}
          </Button>
        ))}
      </Box>

      {/* Filtrowanie po drużynie */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ width: '250px' }}>
          <CustomSelect 
			label='Filtruj po drużynie'
            value={selectedTeamFilter} 
            onChange={handleTeamFilterSelect} 
            options={uniqueTeamsOptions} 
          />
        </Box>
      </Box>

      {/* Kontener tabeli (Zwężony do maxWidth: 600px) */}
      {sortedMatches.length > 0 ? (
        <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
          <TableContainer
            component={Paper}
            sx={{
              marginBottom: 4,
              margin: '0 auto',
              width: '100%',
              overflowX: 'auto',
              borderRadius: '12px',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { width: '8px', height: '8px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
            }}
          >
            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {/* Szerokości dostosowane: 40% - 40% - 20% */}
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: 'background.paper', width: '40%', padding: '10px', fontSize: { xs: '0.8rem', sm: '1rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)' }}
                  >
                    <TableSortLabel active={orderBy === 'homeTeamName'} direction={orderBy === 'homeTeamName' ? order : 'asc'} onClick={() => handleRequestSort('homeTeamName')}>
                      Drużyna A
                    </TableSortLabel>
                  </TableCell>
                  
                  <TableCell
                    align="left"
                    sx={{ backgroundColor: 'background.paper', width: '40%', padding: '10px', fontSize: { xs: '0.8rem', sm: '1rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}
                  >
                    <TableSortLabel active={orderBy === 'awayTeamName'} direction={orderBy === 'awayTeamName' ? order : 'asc'} onClick={() => handleRequestSort('awayTeamName')}>
                      Drużyna B
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ backgroundColor: 'background.paper', width: '20%', padding: '10px', fontSize: { xs: '0.8rem', sm: '1rem' }, borderBottom: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}
                  >
                    <TableSortLabel active={orderBy === 'probability'} direction={orderBy === 'probability' ? order : 'desc'} onClick={() => handleRequestSort('probability')}>
                      Szansa
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMatches.map((row) => (
                  <TableRow key={`${row.homeTeamId}-${row.awayTeamId}`}>
                    <TableCell align="right" sx={{ padding: '8px 10px', fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
                      {row.homeTeamName}
                    </TableCell>
                    
                    <TableCell align="left" sx={{ padding: '8px 10px', fontSize: { xs: '0.8rem', sm: '0.95rem' }, borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                      {row.awayTeamName}
                    </TableCell>
                    
                    <TableCell
                      align="center"
                      title={`Liczba wystąpień w symulacji: ${row.occurrences}`}
                      sx={{
                        backgroundColor: getGradientColor(row.probability, maxProbabilityInStage),
                        color: 'black',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                        padding: '8px 10px',
                        textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                        transition: 'background-color 0.3s',
                        cursor: 'help',
                        '@media (hover: hover)': {
                          '&:hover': {
                            backgroundColor: getGradientColor(row.probability * 1.1, maxProbabilityInStage),
                            transform: 'scale(1.03)',
                          },
                        },
                      }}
                    >
                      {formatChance(row.probability, false)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Typography sx={{ color: '#aaaaaa', textAlign: 'center', mt: 4 }}>
          Brak potencjalnych meczów spełniających wybrane kryteria.
        </Typography>
      )}

    </Box>
  );
};

export default WcMatchesProbabilities;